import { Link } from "react-router-dom";
import { Calendar, HeartHandshake, MessagesSquare, ShieldCheck, Sparkles, Video, ArrowRight, Quote } from "lucide-react";
import Navbar from "../components/Navbar";
import SponsorSection from "../components/SponsorSection";

// Unsplash resize params: `w=<width>&auto=format&q=<compression>` — serves properly-sized WebP
// automatically. Without these the raw `q=85` version can be 1-3 MB and drags the LCP badly.
// We keep the ixid/ixlib/photo-id from the picker so attribution links still work.
const heroImage = "https://images.unsplash.com/photo-1693639257331-0bad8ac3913f?ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxhZnJpY2FuJTIweW91dGglMjBzbWlsaW5nJTIwd2FybXxlbnwwfHx8fDE3ODI3ODI1NjR8MA&ixlib=rb-4.1.0&auto=format&fit=crop&w=960&q=70";
const heroImageLarge = "https://images.unsplash.com/photo-1693639257331-0bad8ac3913f?ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxhZnJpY2FuJTIweW91dGglMjBzbWlsaW5nJTIwd2FybXxlbnwwfHx8fDE3ODI3ODI1NjR8MA&ixlib=rb-4.1.0&auto=format&fit=crop&w=1440&q=72";
const roomImage = "https://images.unsplash.com/photo-1770573318791-510ad9e11391?ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHx0aGVyYXB5JTIwcm9vbSUyMHdhcm0lMjBsaWdodHxlbnwwfHx8fDE3ODI3ODI1NjR8MA&ixlib=rb-4.1.0&auto=format&fit=crop&w=960&q=70";

const Feature = ({ icon: Icon, title, body, testid }) => (
  <div className="brand-card brand-card-hover p-7" data-testid={testid}>
    <div className="w-12 h-12 rounded-2xl bg-secondary/15 text-secondary flex items-center justify-center mb-4">
      <Icon size={22} strokeWidth={2.2} />
    </div>
    <h3 className="font-bold text-xl text-[#3D405B] mb-2">{title}</h3>
    <p className="text-[#5F627D] text-sm leading-relaxed">{body}</p>
  </div>
);

const Stat = ({ value, label }) => (
  <div className="brand-card p-6">
    <div className="text-4xl font-extrabold text-primary">{value}</div>
    <div className="text-xs uppercase tracking-[0.2em] text-[#7A7D96] font-bold mt-1">{label}</div>
  </div>
);

export default function Landing() {
  return (
    <div className="min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-10 lg:pt-16 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-4" data-testid="hero-overline">A Hello Foundation Initiative · South Africa</div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.02] text-[#3D405B]">
              A safe place to talk,
              <br />
              <span className="gradient-text">whenever you're ready.</span>
            </h1>
            <p className="text-lg text-[#5F627D] mt-6 max-w-xl leading-relaxed">
              Hellofoundation Youth Mental Wellness & Support offers <strong className="text-[#3D405B]">free, confidential</strong> counselling for children, teenagers and their caregivers — booked online, met one-on-one, and supported with self-help resources between sessions.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Link to="/register" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary text-white font-bold shadow-md hover:-translate-y-0.5 hover:shadow-lg transition-all" data-testid="hero-cta-register">
                Book your first session <ArrowRight size={18} />
              </Link>
              <Link to="/login" className="px-7 py-3.5 rounded-full font-bold text-[#3D405B] border-2 border-[#3D405B]/15 hover:bg-[#3D405B]/5 transition-all" data-testid="hero-cta-signin">
                I have an account
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-2 text-sm text-[#7A7D96]">
              <ShieldCheck size={16} className="text-secondary" />
              Confidential · Free of charge · Registered counsellors
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] rounded-[2.2rem] overflow-hidden soft-shadow">
              <img
                src={heroImage}
                srcSet={`${heroImage} 960w, ${heroImageLarge} 1440w`}
                sizes="(max-width: 1024px) 100vw, 40vw"
                alt="Smiling young person"
                width={960}
                height={1200}
                loading="eager"
                fetchpriority="high"
                decoding="async"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#3D405B]/55 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 bg-card rounded-2xl p-4 flex items-start gap-3 shadow-lg">
                <Quote size={20} className="text-primary shrink-0 mt-0.5" />
                <p className="text-sm text-[#3D405B] leading-relaxed">
                  <strong>"I didn't think I could talk to anyone."</strong> Through Hellofoundation Youth Mental Wellness & Support, Lerato (16) reached out for the first time.
                </p>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 brand-card p-4 hidden sm:flex items-center gap-3 w-56">
              <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center"><Sparkles size={18} className="text-[#3D405B]" /></div>
              <div className="text-xs"><div className="font-bold text-[#3D405B]">Early intervention</div><div className="text-[#7A7D96]">Before crisis hits</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS / WHY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          <Stat value="Free" label="Cost to you" />
          <Stat value="50min" label="Per session" />
          <Stat value="Safe" label="& confidential" />
          <Stat value="100%" label="Free always" />
        </div>
      </section>

      {/* MISSION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 relative aspect-[5/6] rounded-[2.2rem] overflow-hidden soft-shadow grain">
            <img
              src={roomImage}
              alt="Warm counselling space"
              width={960}
              height={1152}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="lg:col-span-7">
            <div className="overline mb-3">Why this matters</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#3D405B] tracking-tight leading-tight">
              Mental health support shouldn't have a waiting list — or a price tag.
            </h2>
            <p className="text-[#5F627D] mt-5 leading-relaxed">
              Young South Africans face high levels of anxiety, depression, bullying, and exposure to violence. The SAMRC reports that most young people with mental-health challenges never receive any support. Hellofoundation Youth Mental Wellness & Support closes that gap by offering early, accessible, professional counselling — before challenges become crises.
            </p>
            <ul className="mt-6 space-y-3">
              {["One-on-one sessions with a registered counsellor", "Parent and caregiver guidance sessions", "Referral support for specialised care", "Free, confidential, no judgement"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-[#3D405B]">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="overline mb-3">How it works</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#3D405B] tracking-tight">Everything you need in one calm place.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <Feature testid="feature-booking" icon={Calendar} title="Book a session" body="Pick a counsellor, choose a time that fits your day, and get a confirmation instantly. No waiting rooms." />
          <Feature testid="feature-video" icon={Video} title="Talk face-to-face online" body="Each booking includes a private video room — open it from your dashboard at session time." />
          <Feature testid="feature-chat" icon={MessagesSquare} title="Chat between sessions" body="Send a quick message to your counsellor when something is on your mind. Always private." />
          <Feature testid="feature-selfhelp" icon={Sparkles} title="Self-help library" body="Short videos on anxiety, bullying, coping skills, mindfulness and more — curated by our team." />
          <Feature testid="feature-records" icon={HeartHandshake} title="Session records" body="Look back at session summaries your counsellor shares with you, so progress is yours to keep." />
          <Feature testid="feature-confidential" icon={ShieldCheck} title="Confidential by design" body="Your conversations stay between you and your counsellor. Always." />
        </div>
      </section>

      {/* SPONSOR */}
      <SponsorSection />

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <div className="relative overflow-hidden rounded-[2.5rem] p-10 md:p-16 bg-gradient-to-br from-[#E07A5F] via-[#D1664A] to-[#C8593C] text-white soft-shadow">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-accent/30 blur-3xl" />
          <div className="relative max-w-2xl">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">Ready to talk to someone who listens?</h2>
            <p className="mt-4 text-white/90 leading-relaxed">Create a free account in under a minute. Sessions are booked, confidential, and always free.</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/register" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-primary font-bold shadow-md hover:-translate-y-0.5 transition-all" data-testid="footer-cta-register">
                Create my account <ArrowRight size={18} />
              </Link>
              <Link to="/login" className="px-7 py-3.5 rounded-full font-bold border-2 border-white/40 hover:bg-white/10 transition-all">
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#3D405B]/5 py-10 px-4 sm:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-[#7A7D96]">
          <div>© {new Date().getFullYear()} Hello Foundation — Hellofoundation Youth Mental Wellness & Support Youth Counselling Initiative</div>
          <div className="flex items-center gap-4">
            <Link to="/counsellor-apply" className="text-[#3D405B] font-bold hover:text-primary transition-colors" data-testid="footer-counsellor-apply">
              Volunteer as a counsellor →
            </Link>
            <div className="font-semibold text-[#3D405B]">If you are in immediate danger, call SADAG: 0800 567 567</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
